/**
 * Daily work: every loom as one row, the way the old DAILY WORK sheet listed them. A loom with a
 * saree gets an inches box; a closed loom shows why (and can be reopened); a loom with no saree
 * offers Start saree. Built from the rounds response for one day.
 */
import { buildSheet, newRequestId, PersonRef, readyToSave, SheetLine } from "../sheet/sheetModel";
import { backfillOffer, backfillPart, moveStartOffer, MoveStartOffer } from "../sarees/sareeEditModel";

export type DailyKind = "RUN" | "CLOSED" | "IDLE";
export type DailyFilter = "ALL" | "LEFT" | "DONE" | "CLOSED";

export type DailyRow = {
  loomId: string;
  loomCode: string;
  kind: DailyKind;
  sareeNumber?: string;
  designNumber?: string;
  weaver?: PersonRef | null;
  helper?: PersonRef | null;
  /** Inches already saved for this day (all parts together). */
  savedInches: number;
  savedAmount: number;
  savedEntries: number;
  waitingApproval: boolean;
  /** Saved, but at least one entry has no rate yet. */
  unrated: boolean;
  /** Days since the last entry, when nothing is saved for this day yet. */
  daysSince: number | null;
  reason?: string;
  closedSince?: string;
  /** No saree on this day, but one from a later day whose start can't come back to it: offer "Move start" / "Ask the office". */
  moveStart?: MoveStartOffer | null;
  /**
   * No saree on this day, but one from a later day that can start on it: the row takes inches like
   * any other, and saving moves the start (owner/manager) or sends both to Approvals (supervisor).
   */
  startMove?: MoveStartOffer | null;
  /** The loom's helper changed in the last week: its people are confirmed one by one, never in bulk. */
  helperChanged?: boolean;
};

/** "Sat, 24 Oct": the weekday matters more than the year when entering a day's work. */
export const dayLabel = (iso: string, lang: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { weekday: "short", day: "numeric", month: "short" });

/** "31 Oct": a saree's start next to the day being filled. */
export const dayMonth = (iso: string, lang = "en") =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { day: "numeric", month: "short" });

/** "18–24 Oct", or "28 Sep – 4 Oct" across months: a week at a glance. */
export const rangeLabel = (from: string, to: string, lang: string) => {
  const loc = lang === "hi" ? "hi-IN" : "en-IN";
  const a = new Date(`${from}T00:00:00`);
  const b = new Date(`${to}T00:00:00`);
  const month = (d: Date) => d.toLocaleDateString(loc, { month: "short" });
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()}–${b.getDate()} ${month(b)}`
    : `${a.getDate()} ${month(a)} – ${b.getDate()} ${month(b)}`;
};

export const daysBetween = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 864e5);

const plain = (n: number) => String(Math.round(n * 100) / 100);

/**
 * Rows in loom order plus the editable lines (one per loom with a saree). A loom that already has
 * work saved for the day is shown with its saved inches and is not typed into again here; a second
 * part on the same day is added from the loom's own page.
 */
export const buildDaily = (rounds: any, defaultWorkTypeId: string, date: string): { rows: DailyRow[]; lines: SheetLine[] } => {
  const sheet = buildSheet(rounds, defaultWorkTypeId);
  const lineByLoom = new Map(sheet.lines.map((l) => [l.loomId, l]));
  const rows: DailyRow[] = [];
  const lines: SheetLine[] = [];
  for (const l of rounds?.looms || []) {
    const entries = (l.entries || []).filter((e: any) => e.status !== "VOID" && e.status !== "REJECTED");
    const savedInches = entries.reduce((s: number, e: any) => s + (Number(e.inches) || 0), 0);
    const savedAmount = entries.reduce((s: number, e: any) => s + (Number(e.calc?.total) || 0), 0);
    const offer = !l.assignment && l.state !== "CLOSED" ? moveStartOffer(l) : null;
    // A later saree that can start on this day is filled in like a running loom.
    const backfill = backfillOffer(l);
    const a = l.assignment || (backfill ? l.laterSaree : null);
    const kind: DailyKind = l.state === "CLOSED" ? "CLOSED" : a ? "RUN" : "IDLE";
    const last: string | undefined = backfill ? undefined : l.lastEntryDate || undefined;
    const lastPeople = backfill ? null : l.lastPeople;
    const alone = lastPeople ? lastPeople.helperMode !== "WITH_HELPER" : a?.helperMode !== "WITH_HELPER";
    rows.push({
      loomId: l.id,
      loomCode: String(l.code),
      kind,
      sareeNumber: a?.sareeNumber,
      designNumber: a?.designNumber,
      weaver: lastPeople?.weaver || a?.weaver || null,
      helper: alone ? null : lastPeople?.helper || a?.helper || null,
      savedInches,
      savedAmount,
      savedEntries: entries.length,
      waitingApproval: entries.some((e: any) => e.status === "PENDING_APPROVAL"),
      unrated: entries.some((e: any) => e.calc?.status && e.calc.status !== "OK"),
      daysSince: kind === "RUN" && !entries.length && last && last < date ? daysBetween(last, date) : null,
      reason: l.closure?.reason,
      closedSince: l.closure?.from,
      moveStart: kind === "IDLE" ? offer : null,
      startMove: backfill,
      helperChanged: !!l.helperChangedRecently,
    });
    const line = backfill ? laterLine(l, a, backfill) : lineByLoom.get(l.id);
    if (kind === "RUN" && line) {
      lines.push(entries.length ? { ...line, status: "SAVED", inches: plain(savedInches), amount: plain(savedAmount) } : line);
    }
  }
  return { rows, lines };
};

/** The line for a loom whose later saree can start on this day: its people, its part (or none yet), and the move. */
const laterLine = (l: any, later: any, offer: MoveStartOffer): SheetLine => {
  const helper = later?.helperMode === "WITH_HELPER" ? later?.helper : null;
  const newest = (l.entries || []).reduce((n: string, e: any) => (e.status !== "VOID" && e.status !== "REJECTED" && String(e.id) > n ? String(e.id) : n), "");
  return {
    key: l.id,
    loomId: l.id,
    loomCode: l.code,
    registerId: l.register?.loomCode,
    sareeNumber: later?.sareeNumber,
    designNumber: later?.designNumber,
    weaverId: later?.weaver?.id || "",
    helperId: helper?.id || "",
    helperMode: later?.helperMode === "NOT_NEEDED" ? "NOT_NEEDED" : "ALONE",
    // Never the studio's default part: the later saree's own starting part, or picked by hand.
    workTypeId: backfillPart(l),
    inches: "",
    requestId: newRequestId(),
    afterEntryId: newest,
    status: "EDIT",
    moveStart: { pieceId: offer.pieceId as string, from: offer.currentStart },
  };
};

/** After a reload, keep what was typed but not saved yet (inches, people, part) on looms still open. */
export const keepTyped = (fresh: SheetLine[], before: SheetLine[]): SheetLine[] => {
  const old = new Map(before.filter((l) => l.status === "EDIT" || l.status === "ERROR").map((l) => [l.loomId, l]));
  return fresh.map((l) => {
    const o = old.get(l.loomId);
    // Someone else saved this loom meanwhile: show their entry, and say why ours wasn't saved.
    if (o && l.status === "SAVED") return o.status === "ERROR" && o.message ? { ...l, message: o.message } : l;
    if (!o) return l;
    return { ...l, inches: o.inches, weaverId: o.weaverId, helperId: o.helperId, helperMode: o.helperMode, workTypeId: o.workTypeId || l.workTypeId, requestId: o.requestId, status: o.status, message: o.message };
  });
};

export const isDone = (r: DailyRow) => r.kind === "RUN" && r.savedEntries > 0;
export const isLeft = (r: DailyRow) => r.kind === "RUN" && r.savedEntries === 0;

export const countRows = (rows: DailyRow[]): Record<DailyFilter, number> => ({
  ALL: rows.length,
  LEFT: rows.filter(isLeft).length,
  DONE: rows.filter(isDone).length,
  CLOSED: rows.filter((r) => r.kind === "CLOSED").length,
});

export const matchesFilter = (r: DailyRow, f: DailyFilter): boolean =>
  f === "ALL" || (f === "LEFT" && isLeft(r)) || (f === "DONE" && isDone(r)) || (f === "CLOSED" && r.kind === "CLOSED");

export type RowTone = "ok" | "warn" | "bad" | "muted" | "info" | "err";
export type RowStatus = { tone: RowTone; label: string; n?: number } | null;

/**
 * Weaver and helper are never carried over silently: the people pre-filled from last time count only once
 * someone confirms them ("Same as yesterday") or changes them on this screen. `confirmed` holds those looms.
 */
const PEOPLE_KEYS = ["weaverId", "helperId", "helperMode"];

/** True when an edit sets the weaver, helper or alone/not-needed: that counts as confirming the people. */
export const changesPeople = (p: Partial<SheetLine>): boolean => PEOPLE_KEYS.some((k) => k in p);

/** Looms that need their people confirmed before they can be saved (unsaved, with a saree). */
export const needsPeople = (r: DailyRow, line: SheetLine | undefined, confirmed: Set<string>): boolean =>
  r.kind === "RUN" && !!line && line.status !== "SAVED" && line.status !== "SAVING" && !confirmed.has(r.loomId);

/** Lines ready to save: inches typed and the people confirmed or changed. */
export const savable = (lines: SheetLine[], confirmed: Set<string>): SheetLine[] =>
  readyToSave(lines).filter((l) => confirmed.has(l.loomId));

/** A line with inches typed but no part chosen (a back-filled saree with no known starting part): pick it first. */
export const needsPart = (line: SheetLine | undefined): boolean =>
  !!line && line.status !== "SAVED" && line.status !== "SAVING" && !line.workTypeId;

/** Lines with inches typed that still wait for their part to be picked. */
export const waitingForPart = (lines: SheetLine[]): SheetLine[] =>
  lines.filter((l) => needsPart(l) && Number(l.inches) > 0);

/** Lines with inches typed that still wait for their people to be confirmed. */
export const waitingForPeople = (lines: SheetLine[], confirmed: Set<string>): SheetLine[] =>
  readyToSave(lines).filter((l) => !confirmed.has(l.loomId));

/**
 * After a reload, a loom stays confirmed only if the people it starts from are the same as before
 * (someone saving or changing people elsewhere means asking again).
 */
export const keepConfirmed = (confirmed: Set<string>, before: DailyRow[], after: DailyRow[]): Set<string> => {
  const sig = (r?: DailyRow) => (r ? `${r.sareeNumber || ""}|${r.weaver?.id || ""}|${r.helper?.id || ""}` : "");
  const old = new Map(before.map((r) => [r.loomId, sig(r)]));
  const next = new Set<string>();
  after.forEach((r) => {
    if (confirmed.has(r.loomId) && old.get(r.loomId) === sig(r)) next.add(r.loomId);
  });
  return next;
};

/** What the status column says for a row. `preview` is the live pay for typed inches, when known. */
export const rowStatus = (row: DailyRow, line?: SheetLine, preview?: { status?: string } | null, peopleOk = true): RowStatus => {
  if (row.kind !== "RUN") return null;
  if (line?.status === "SAVING") return { tone: "muted", label: "Saving…" };
  if (line?.status === "ERROR") return { tone: "err", label: line.message || "Not saved" };
  if (row.savedEntries > 0) {
    if (row.waitingApproval) return { tone: "info", label: "Waiting" };
    if (row.unrated) return { tone: "warn", label: "No approved rate" };
    return { tone: "ok", label: "Saved" };
  }
  if (line && Number(line.inches) > 0) {
    if (!line.workTypeId) return { tone: "warn", label: "Choose the part" };
    if (!peopleOk) return { tone: "warn", label: "Confirm people" };
    if (preview?.status && preview.status !== "OK") return { tone: "warn", label: "No approved rate" };
    return { tone: "muted", label: "Typing" };
  }
  if (row.daysSince && row.daysSince > 0) return { tone: row.daysSince >= 4 ? "bad" : "warn", label: row.daysSince === 1 ? "1 day" : "{{n}} days", n: row.daysSince };
  return null;
};
